import {
  Block,
  TableContent,
  TextBlock,
} from "@prismicio/types-internal/lib/content"
import { Table as TTTable } from "@tiptap/extension-table"
import { TableCell as TableCellNode } from "@tiptap/extension-table-cell"
import { TableHeader as TableHeaderNode } from "@tiptap/extension-table-header"
import { TableRow as TableRowNode } from "@tiptap/extension-table-row"
import type { Node as ProseMirrorNode, Schema } from "@tiptap/pm/model"
import {
  addColumn,
  addRow,
  isInTable,
  selectedRect,
  TableView,
} from "@tiptap/pm/tables"
import { type CommandProps, Node, ReactNodeViewRenderer } from "@tiptap/react"

import { NodeUtils, TableExtension } from "../../models"
import { ExtendedRichTextNodeType, type TableBlock } from "../../types"
import { Paragraph } from "../extensions"
import { TableControlsWrapper } from "./TableControlsWrapper"

type TableRowBlock = TableContent["content"][number]
type TableCellOrHeaderBlock = TableRowBlock["content"][number]

function getNodeFromSchema(schema: Schema, name: string) {
  const node = schema.nodes[name]
  if (!node) {
    // This will only happen if RichTextField is not properly configured with
    // the necessary extensions (table, tableRow, tableHeader and tableCell),
    // so it should not be handling tables.
    throw new Error(`Unwanted state: ${name} not found in schema.`)
  }
  return node
}

function tableCellToParagraphMapper(schema: Schema) {
  return (data: Block) => {
    switch (data.type) {
      case "hyperlink":
      case "paragraph":
      case "strong":
      case "em": {
        return Paragraph.converter.fromPrismic(schema)(data) || []
      }
      default: {
        return []
      }
    }
  }
}

const tableCellConverter = {
  fromPrismic(schema: Schema) {
    return function (cell: TableCellOrHeaderBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TableCellNode.name)
      return node.create(
        { colspan: 1, rowspan: 1, colwidth: null },
        cell.content.value.flatMap(tableCellToParagraphMapper(schema)),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableCellOrHeaderBlock {
    const content: TextBlock[] = []
    node.content.forEach((node) => {
      const textBlock = Paragraph.converter.toPrismic(node)
      if (TextBlock.is(textBlock)) content.push(textBlock)
    })

    return {
      type: "tableCell",
      content: { __TYPE__: "StructuredTextContent", value: content },
    }
  },
}

const tableHeaderConverter = {
  fromPrismic(schema: Schema) {
    return function (header: TableCellOrHeaderBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TableHeaderNode.name)
      return node.create(
        { colspan: 1, rowspan: 1, colwidth: null },
        header.content.value.flatMap(tableCellToParagraphMapper(schema)),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableCellOrHeaderBlock {
    const content: TextBlock[] = []
    node.content.forEach((node) => {
      const textBlock = Paragraph.converter.toPrismic(node)
      if (TextBlock.is(textBlock)) content.push(textBlock)
    })

    return {
      type: "tableHeader",
      content: { __TYPE__: "StructuredTextContent", value: content },
    }
  },
}

const tableRowConverter = {
  fromPrismic(schema: Schema) {
    return function (row: TableRowBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TableRowNode.name)
      return node.create(
        {},
        row.content.map((data) => {
          switch (data.type) {
            case "tableHeader": {
              return tableHeaderConverter.fromPrismic(schema)(data)
            }
            case "tableCell": {
              return tableCellConverter.fromPrismic(schema)(data)
            }
          }
        }),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableRowBlock {
    const content: TableCellOrHeaderBlock[] = []
    node.content.forEach((cell) => {
      switch (cell.type.name) {
        case "tableHeader": {
          content.push(tableHeaderConverter.toPrismic(cell))
          break
        }
        case "tableCell": {
          content.push(tableCellConverter.toPrismic(cell))
          break
        }
      }
    })

    return { type: "tableRow", content }
  },
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    Table: {
      /**
       * Add a row at the end of the table
       * @returns True if the command was successful, otherwise false
       * @example editor.commands.addRowToEnd()
       */
      addRowToEnd: () => ReturnType
      /**
       * Add a column at the end of the table
       * @returns True if the command was successful, otherwise false
       * @example editor.commands.addColumnToEnd()
       */
      addColumnToEnd: () => ReturnType
    }
  }
}

const TableNode = TTTable.extend({
  name: "innerTable",
  group: "table",

  addNodeView() {
    return ({ node }) => new TableView(node, 120)
  },
  addCommands() {
    return {
      ...this.parent?.(),
      addRowToEnd: () => {
        return ({ dispatch, state }: CommandProps) => {
          if (!isInTable(state)) return false
          if (dispatch) {
            const rect = selectedRect(state)
            const rowCount = rect.map.height
            dispatch(addRow(state.tr, rect, rowCount))
          }
          return true
        }
      },
      addColumnToEnd: () => {
        return ({ dispatch, state }: CommandProps) => {
          if (!isInTable(state)) return false
          if (dispatch) {
            const rect = selectedRect(state)
            const colCount = rect.map.width
            dispatch(addColumn(state.tr, rect, colCount))
          }
          return true
        }
      },
    }
  },
})

const tableConverter = {
  fromPrismic(schema: Schema) {
    return function (table: TableBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TableNode.name)
      const tableNode = node.create(
        {},
        table.content.map(tableRowConverter.fromPrismic(schema)),
      )

      const tableWithControls = schema.nodes[TableControlsWrapperNode.name]
      if (tableWithControls) return tableWithControls.create({}, tableNode)

      return tableNode
    }
  },

  toPrismic(node: ProseMirrorNode): TableBlock {
    // If the table has controls, the first node will be the wrapper with the controls.
    const firstTableNode =
      node.content.firstChild?.type.name === TableNode.name
        ? node.content.firstChild
        : node

    return {
      type: "table",
      content: NodeUtils.children.map<TableRowBlock, false>(
        firstTableNode,
        (row) => tableRowConverter.toPrismic(row),
      ),
    }
  },
}

const TableControlsWrapperNode = Node.create({
  // When using this wrapper, it has to be named table, as the onUpdate event
  // will be fired for the top level nodes, and we need to have matching names.
  name: "table",
  group: "block",
  content: "innerTable",
  selectable: false,

  parseHTML() {
    return [{ tag: "div" }]
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", HTMLAttributes, 0]
  },
  addNodeView() {
    return ReactNodeViewRenderer(TableControlsWrapper)
  },
})

export const Table = new TableExtension(
  [ExtendedRichTextNodeType.table],
  ExtendedRichTextNodeType.table,
  [
    TableControlsWrapperNode,
    TableNode,
    TableRowNode,
    TableHeaderNode,
    TableCellNode,
  ],
  tableConverter,
)
